import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

/**
 * Component for managing round-robin assignment groups
 */
const AssignmentGroupsSettings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [newGroup, setNewGroup] = useState({
    groupName: '',
    userIds: []
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    groupName: ''
  });

  // Fetch assignment groups and users when component mounts
  useEffect(() => {
    fetchAssignmentGroups();
    fetchUsers();
  }, []);

  // Fetch assignment groups from API
  const fetchAssignmentGroups = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const config = {
        headers: {
          'x-auth-token': token
        }
      };

      const response = await axios.get('/api/automation/assignment-groups', config);
      setAssignmentGroups(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching assignment groups:', err);
      setError('Failed to load assignment groups: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Fetch users for group configuration
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = {
        headers: {
          'x-auth-token': token
        }
      };

      const response = await axios.get('/api/auth/users', config);
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Handle form input changes for new group
  const handleNewGroupChange = (e) => {
    const { name, value } = e.target;
    setNewGroup(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle multi-select for user IDs
  const handleUserSelectionChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
    setNewGroup(prev => ({
      ...prev,
      userIds: selectedOptions
    }));
  };

  // Handle edit group
  const handleEditGroup = (group) => {
    // Prepare the selected users
    const userIds = group.users ? group.users.map(user => user.id) : [];
    
    setNewGroup({
      groupName: group.group_name,
      userIds: userIds
    });
    
    setIsEditMode(true);
    setEditGroupName(group.group_name); // Save original name for update endpoint
    setShowGroupModal(true);
  };

  // Submit group (new or edit)
  const handleSubmitGroup = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!newGroup.groupName || !newGroup.userIds || newGroup.userIds.length === 0) {
      setError('Please provide a group name and select at least one user');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      const payload = {
        groupName: newGroup.groupName,
        userIds: newGroup.userIds
      };

      if (isEditMode) {
        // Use PUT to update the group
        await axios.put(
          `/api/automation/assignment-groups/${editGroupName}`,
          payload,
          config
        );
        setSuccess(`Assignment group "${newGroup.groupName}" updated successfully`);
      } else {
        // Use POST to create a new group
        await axios.post(
          '/api/automation/assignment-groups',
          payload,
          config
        );
        setSuccess('New assignment group created successfully');
      }

      // Refresh the assignment groups list
      await fetchAssignmentGroups();

      // Reset form and close modal
      setNewGroup({
        groupName: '',
        userIds: []
      });

      setShowGroupModal(false);
      setIsEditMode(false);
      setEditGroupName('');
      setLoading(false);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error saving assignment group:', err);
      setError('Failed to save group: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (groupName) => {
    setDeleteModal({
      isOpen: true,
      groupName
    });
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      groupName: ''
    });
  };

  // Handle deleting a group
  const handleDeleteGroup = async (groupName) => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };

      await axios.delete(`/api/automation/assignment-groups/${groupName}`, config);

      // Refresh the assignment groups list
      await fetchAssignmentGroups();

      setSuccess(`Assignment group "${groupName}" deleted successfully`);
      setLoading(false);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error deleting assignment group:', err);
      setError('Failed to delete group: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900">Round-Robin Assignment Groups</h3>
        <button
          onClick={() => {
            setNewGroup({
              groupName: '',
              userIds: []
            });
            setIsEditMode(false);
            setEditGroupName('');
            setShowGroupModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-sm rounded-md flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Group
        </button>
      </div>

      {/* Display error or success messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Show loading state */}
      {loading && (
        <div className="mb-4 px-4 py-3 text-blue-600">
          Loading...
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned Users
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Index
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {assignmentGroups.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  No assignment groups found. Create a new group to get started.
                </td>
              </tr>
            ) : (
              assignmentGroups.map((group) => (
                <tr key={group.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{group.group_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {group.users && group.users.length > 0 ? (
                        <ul>
                          {group.users.map(user => (
                            <li key={user.id}>{user.name}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="italic">No users assigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{group.last_assigned_index}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-4 justify-end">
                      <button
                        onClick={() => handleEditGroup(group)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openDeleteModal(group.group_name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden mb-6 p-4">
        <h4 className="text-md font-medium text-gray-900 mb-2">How to use Round-Robin Assignment</h4>
        <p className="text-sm text-gray-600 mb-2">
          Round-robin assignment groups automatically distribute leads among selected sales reps in a fair, rotating manner.
        </p>
        <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-1">
          <li>Create a group with a meaningful name (e.g., "dach_plus" for DACH region leads)</li>
          <li>Add the sales reps who should be part of this rotation</li>
          <li>Create an automation rule with the "round_robin_assign" action type</li>
          <li>In the rule's action settings, specify the group_name</li>
        </ol>
      </div>

      {/* Group Modal (New/Edit) */}
      {showGroupModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      {isEditMode ? 'Edit Assignment Group' : 'Create New Assignment Group'}
                    </h3>

                    <form onSubmit={handleSubmitGroup}>
                      <div className="mb-4">
                        <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 mb-1">
                          Group Name *
                        </label>
                        <input
                          type="text"
                          name="groupName"
                          id="groupName"
                          required
                          placeholder="e.g., dach_plus"
                          value={newGroup.groupName}
                          onChange={handleNewGroupChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md bg-white text-gray-900"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Use lowercase letters and underscores. This will be used in automation rules.
                        </p>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="userIds" className="block text-sm font-medium text-gray-700 mb-1">
                          Select Users *
                        </label>
                        <select
                          id="userIds"
                          multiple
                          required
                          value={newGroup.userIds}
                          onChange={handleUserSelectionChange}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md bg-white text-gray-900 h-40"
                        >
                          {users.map(user => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Hold Ctrl/Cmd to select multiple users. These users will receive leads in rotation.
                        </p>
                      </div>

                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                          {isEditMode ? 'Update Group' : 'Create Group'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowGroupModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={() => {
          handleDeleteGroup(deleteModal.groupName);
          closeDeleteModal();
        }}
        title="Confirm Delete"
        message={`Are you sure you want to delete the "${deleteModal.groupName}" assignment group? This action cannot be undone.`}
      />
    </div>
  );
};

export default AssignmentGroupsSettings;